// v2.27 — Profil → Doublons d'élèves. Rien d'automatique : pour chaque paire, Thierry choisit
// Fusionner (en gardant la fiche qu'il veut), Pas un doublon, ou Supprimer une fiche. Tout est annulable.
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api';
import { cn } from '../../lib/utils';

const fmtDate = (iso) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—');
const countsLine = (c = {}) => `${c.attendances || 0} présence${(c.attendances || 0) > 1 ? 's' : ''} · ${c.payments || 0} paiement${(c.payments || 0) > 1 ? 's' : ''} · ${c.courses || 0} cours`;

function Fiche({ s, kept, onKeep, onDelete }) {
  return (
    <div className={cn('rounded-xl border p-3 text-sm min-w-0', kept ? 'border-[#26A69A] bg-[#26A69A]/5' : 'border-slate-200 bg-white')}>
      <label className="flex items-center gap-2 font-bold text-slate-900 cursor-pointer">
        <input type="radio" checked={kept} onChange={onKeep} data-testid="dup-keep" />
        <span className="truncate">{s.name}</span>
      </label>
      <div className="mt-1 text-xs text-slate-500 space-y-0.5 break-all">
        <div>{s.email || <span className="italic">pas d'email</span>}</div>
        <div>{s.phone || <span className="italic">pas de téléphone</span>}</div>
        {s.created_at && <div>Créée le {fmtDate(s.created_at)}</div>}
        <div className="font-semibold text-slate-700">{countsLine(s.counts)}</div>
      </div>
      <button onClick={onDelete} className="mt-2 text-xs font-semibold text-red-500 underline" data-testid="dup-delete">Supprimer cette fiche</button>
    </div>
  );
}

function Pair({ p, onDone }) {
  const [keepId, setKeepId] = useState(p.keep_id);
  const [busy, setBusy] = useState(false);
  const keep = keepId === p.a.id ? p.a : p.b;
  const other = keepId === p.a.id ? p.b : p.a;
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(e.message || 'Erreur'); } finally { setBusy(false); }
  };
  const merge = () => {
    if (!window.confirm(`Garder « ${keep.name} ».\nTout ce qui concerne l'autre fiche (${countsLine(other.counts)}) passe sur elle.\nLes champs vides sont complétés, rien n'est écrasé.\nAnnulable ensuite.`)) return;
    run(() => api.post('/students/merge', { keep_id: keep.id, remove_id: other.id }), `Fiches fusionnées dans « ${keep.name} »`);
  };
  const del = (s) => {
    if (!window.confirm(`Supprimer la fiche « ${s.name} » et tout ce qui la concerne (${countsLine(s.counts)}) ?\nElle est archivée : tu peux annuler ensuite.`)) return;
    run(() => api.post(`/students/${s.id}/archive-delete`), `Fiche « ${s.name} » supprimée (annulable)`);
  };
  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3 space-y-2" data-testid="dup-pair">
      <div className="flex flex-wrap gap-1.5">
        {p.reasons.map(r => <span key={r} className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{r}</span>)}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Fiche s={p.a} kept={keepId === p.a.id} onKeep={() => setKeepId(p.a.id)} onDelete={() => del(p.a)} />
        <Fiche s={p.b} kept={keepId === p.b.id} onKeep={() => setKeepId(p.b.id)} onDelete={() => del(p.b)} />
      </div>
      <div className="text-xs text-slate-500">
        Mon conseil : garder <b>{(p.keep_id === p.a.id ? p.a : p.b).name}</b> ({p.why}).
      </div>
      <div className="flex flex-wrap gap-2">
        <button disabled={busy} onClick={merge} data-testid="dup-merge"
          className="h-10 px-4 rounded-xl bg-[#26A69A] text-white text-sm font-bold disabled:opacity-50">Fusionner (garder « {keep.name} »)</button>
        <button disabled={busy} data-testid="dup-ignore"
          onClick={() => run(() => api.post('/students/duplicates/ignore', { a: p.a.id, b: p.b.id }), 'Noté : pas un doublon')}
          className="h-10 px-4 rounded-xl bg-white border border-slate-200 text-slate-600 text-sm font-semibold disabled:opacity-50">Pas un doublon</button>
      </div>
    </div>
  );
}

export default function DuplicatesPanel() {
  const [pairs, setPairs] = useState(null);
  const [history, setHistory] = useState([]);
  const load = () => {
    api.get('/students/duplicates').then(setPairs).catch(() => setPairs([]));
    api.get('/students/merges').then(setHistory).catch(() => setHistory([]));
  };
  useEffect(load, []);
  const undo = async (h) => {
    if (!window.confirm(`Annuler : ${h.type === 'merge' ? `fusion de « ${h.name} » dans « ${h.keep_name} »` : `suppression de « ${h.name} »`} ?`)) return;
    try { await api.post(`/students/merges/${h.id}/undo`); toast.success('Annulé : tout est revenu comme avant'); load(); }
    catch (e) { toast.error(e.message || 'Erreur'); }
  };
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mb-4 space-y-3" data-testid="duplicates-section">
      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">👥 Doublons d'élèves</div>
      <p className="text-xs text-slate-500">Fiches qui se ressemblent (même nom, prénom sans nom, même téléphone ou email). Rien n'est fait automatiquement : tu choisis pour chaque paire.</p>
      {pairs === null ? <div className="text-sm text-slate-400">Recherche…</div>
        : pairs.length === 0 ? <div className="text-sm text-[#26A69A] font-semibold">Aucun doublon trouvé ✓</div>
        : pairs.map(p => <Pair key={`${p.a.id}-${p.b.id}`} p={p} onDone={load} />)}
      {history.length > 0 && (
        <div className="pt-2 border-t border-slate-100">
          <div className="text-xs font-bold text-slate-500 mb-1.5">Fusions et suppressions récentes</div>
          {history.map(h => (
            <div key={h.id} className="flex items-center gap-2 text-xs py-1" data-testid="dup-history">
              <span className="flex-1 min-w-0 truncate">
                {fmtDate(h.at)} · {h.type === 'merge' ? `« ${h.name} » fusionnée dans « ${h.keep_name} »` : `« ${h.name} » supprimée`}
              </span>
              <button onClick={() => undo(h)} className="font-bold text-[#0288D1] underline shrink-0" data-testid="dup-undo">Annuler</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
